import React, { useState } from 'react';
import { Bell, X, Check, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import pushNotificationManager from '../utils/pushNotifications';
import './PushNotificationModal.css';

const PushNotificationModal = ({ isOpen, onClose, onSuccess, colors }) => {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, denied
  const [errorMessage, setErrorMessage] = useState('');

  const handleEnable = async () => {
    setStatus('loading');
    
    try {
      // Check browser support
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setStatus('error');
        setErrorMessage('Il tuo browser non supporta le notifiche push');
        return;
      }

      // Check if we're on a third-party domain
      if (pushNotificationManager.isThirdPartyDomain()) {
        console.log('Third-party domain detected, using popup approach...');
        
        // Mark as asked
        pushNotificationManager.markAsAsked();
        
        // Use popup approach for third-party domains
        const result = await pushNotificationManager.openSubscriptionPopup();
        
        if (result && result.success) {
          setStatus('success');
          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 1500);
        } else if (result && result.denied) {
          setStatus('denied');
        } else {
          setStatus('error');
          setErrorMessage(result?.error || 'Errore durante la configurazione');
        }
        return;
      }

      // Same-origin domain - use direct registration
      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'denied') {
        setStatus('denied');
        return;
      }

      if (permission !== 'granted') {
        setStatus('denied');
        return;
      }

      // Mark as asked
      pushNotificationManager.markAsAsked();

      // Try to register service worker and subscribe
      const registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
      await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: pushNotificationManager.urlBase64ToUint8Array(pushNotificationManager.vapidPublicKey)
      });

      // Save subscription to backend
      const saved = await pushNotificationManager.saveSubscription(subscription);
      
      if (saved) {
        setStatus('success');
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1500);
      } else {
        setStatus('error');
        setErrorMessage('Errore nel salvataggio. Riprova piu tardi.');
      }
    } catch (error) {
      console.error('Push notification error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Si e verificato un errore');
    }
  };

  const handleClose = () => {
    pushNotificationManager.markAsAsked();
    onClose();
  };

  const primaryColor = colors?.primary || '#667eea';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="mia-push-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="mia-push-modal"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.2, type: 'spring', damping: 25 }}
          >
            {/* Close button */}
            <button className="mia-push-modal-close" onClick={handleClose}>
              <X size={20} />
            </button>

            {/* Content based on status */}
            {status === 'idle' && (
              <>
                <div 
                  className="mia-push-modal-icon"
                  style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
                >
                  <Bell size={32} color="white" />
                </div>
                <h3 className="mia-push-modal-title">Attiva le notifiche</h3>
                <p className="mia-push-modal-text">
                  Ricevi una notifica quando ti rispondiamo, cosi non perdi nessun messaggio!
                </p>
                <button 
                  className="mia-push-modal-btn-primary"
                  onClick={handleEnable}
                  style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
                >
                  <Bell size={18} />
                  Attiva notifiche
                </button>
                <button className="mia-push-modal-btn-secondary" onClick={handleClose}>
                  Non ora
                </button>
              </>
            )}

            {status === 'loading' && (
              <>
                <div className="mia-push-modal-spinner" style={{ borderTopColor: primaryColor }} />
                <p className="mia-push-modal-text">Configurazione in corso...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mia-push-modal-icon mia-push-modal-icon-success">
                  <Check size={32} color="white" />
                </div>
                <h3 className="mia-push-modal-title">Notifiche attivate!</h3>
                <p className="mia-push-modal-text">
                  Riceverai una notifica quando ci sara un nuovo messaggio.
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="mia-push-modal-icon mia-push-modal-icon-error">
                  <AlertCircle size={32} color="white" />
                </div>
                <h3 className="mia-push-modal-title">Errore</h3>
                <p className="mia-push-modal-text">{errorMessage}</p>
                <button className="mia-push-modal-btn-secondary" onClick={handleClose}>
                  Chiudi
                </button>
              </>
            )}

            {status === 'denied' && (
              <>
                <div className="mia-push-modal-icon mia-push-modal-icon-warning">
                  <AlertCircle size={32} color="white" />
                </div>
                <h3 className="mia-push-modal-title">Permesso negato</h3>
                <p className="mia-push-modal-text">
                  Per ricevere notifiche, abilita i permessi nelle impostazioni del browser.
                </p>
                <button className="mia-push-modal-btn-secondary" onClick={handleClose}>
                  Ho capito
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PushNotificationModal;
